import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  LOCALE_ID,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CountryDto } from '@assetaccountant/api/models/country.dto';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { OrganisationV2Api } from '@assetaccountant/api/services/organisation-v2.api';
import { RegisterApi } from '@assetaccountant/api/services/register.api';
import { Register } from '@assetaccountant/core/models/register';
import { OrganisationResolver } from '@assetaccountant/core/resolvers/organisation.resolver';
import { LayoutService } from '@assetaccountant/core/services/layout.service';
import { SubscriptionService } from '@assetaccountant/core/services/subscription.service';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';
import {
  TypedFormBuilder,
  TypedFormControl,
  TypedFormGroup,
  emptyValue,
} from '@assetaccountant/core/utils/typed-forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-edit-organisation-modal',
  templateUrl: './edit-organisation-modal.component.html',
  styleUrls: ['./edit-organisation-modal.component.scss'],
  standalone: false,
})
export class EditOrganisationModalComponent implements OnDestroy {
  @ViewChild('content') content: ElementRef;
  @Output() onAccepted = new EventEmitter<Register>();

  countries: CountryDto[] = [];
  useOwnDetails = false;

  form: TypedFormGroup<{
    id: TypedFormControl<number>;
    name: TypedFormControl<string>;
    countryId: TypedFormControl<number>;
    description: TypedFormControl<string>;
    billingContactDetails: TypedFormGroup<{
      name: TypedFormControl<string>;
      email: TypedFormControl<string>;
      phoneNumber: TypedFormControl<string>;
    }>;
  }>;
  // Temporary measure - need to tidy up API models
  organisation: OrganisationDto;
  modal: NgbModalRef;

  constructor(
    private fb: TypedFormBuilder,
    private modalService: NgbModal,
    @Inject(LOCALE_ID) public locale: string,
    private organisationApi: OrganisationV2Api,
    private router: Router,
    private subscriptionService: SubscriptionService,
    private registerApi: RegisterApi,
    private trackingService: TrackingService,
    private layoutService: LayoutService,
  ) {}

  onExitClick() {
    if (this.content.nativeElement && this.content.nativeElement.style) {
      this.content.nativeElement.style.display = 'none';
    }
  }

  ngOnDestroy(): void {
    if (this.content.nativeElement) {
      this.content.nativeElement.remove();
    }
  }

  onSubmit() {
    const data = this.form.value;
    this.form.disable();

    this.organisationApi
      .createOrganisation({ ...data, source: this.trackingService.getMarketingSource() })
      .subscribe({
        next: async ({ organisation }) => {
          OrganisationResolver.clearCache();

          this.trackingService.trackEvent('Create Organisation', null, {
            OrganisationId: organisation.id.toString(),
          });

          this.organisationApi
            .createDemoRegister(organisation.id)
            .subscribe(() => this.layoutService.demoRegisterCreated.next());

          this.subscriptionService.reset();

          const navigated = await this.router.navigate([`/organisations/${organisation.id}/start`]);

          if (navigated) {
            this.modal.close();
          }
          this.form.enable();
        },
        error: () => this.form.enable(),
      });
  }

  open() {
    this.organisation = {
      name: '',
      description: '',
    } as OrganisationDto;

    this.registerApi.getCountries().subscribe(result => {
      this.countries = result;
    });

    this.form = this.fb.group({
      id: this.organisation.organisationId,
      name: [this.organisation.name, [Validators.required, Validators.maxLength(50)]],
      description: [this.organisation.description || '', [Validators.maxLength(70)]],
      countryId: [emptyValue<number>(), Validators.required],
      billingContactDetails: this.fb.group({
        name: [{ value: '', disabled: this.useOwnDetails }, Validators.required],
        email: [
          { value: '', disabled: this.useOwnDetails },
          [Validators.required, Validators.email],
        ],
        phoneNumber: [
          { value: '', disabled: this.useOwnDetails },
          [Validators.minLength(8), Validators.maxLength(30)],
        ],
      }),
    });

    // eslint-disable-next-line setup-modal/setup-modal
    this.modal = this.modalService.open(this.content);
  }

  checkUseOwnDetails(event) {
    this.useOwnDetails = event.target.checked;

    if (this.useOwnDetails) {
      this.form.controls.billingContactDetails.disable();
    } else {
      this.form.controls.billingContactDetails.enable();
    }
  }
}
